
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Globe, Copy, Share, RotateCcw, MessageSquarePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { TopicType } from '@/types/assistant';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

interface MessageFeedbackProps {
  messageId: string;
  currentFeedback: 'positive' | 'negative' | null;
  onFeedbackChange: (messageId: string, feedback: 'positive' | 'negative' | null) => void;
  topic: TopicType;
  disabled?: boolean;
  isMultiLanguageSource?: boolean;
  sourceLanguage?: string;
  messageContent?: string; // New prop for message actions
  onRegenerateMessage?: (messageId: string) => void; // New prop for regeneration
}

export const MessageFeedback: React.FC<MessageFeedbackProps> = React.memo(({
  messageId,
  currentFeedback,
  onFeedbackChange,
  topic,
  disabled = false,
  isMultiLanguageSource = false,
  sourceLanguage,
  messageContent,
  onRegenerateMessage
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFeedback = (feedback: 'positive' | 'negative') => {
    // Toggle feedback if already selected
    const newFeedback = currentFeedback === feedback ? null : feedback;
    onFeedbackChange(messageId, newFeedback);
    
    // Show feedback toast
    toast({
      description: newFeedback === 'positive' 
        ? t('ai_assistant.feedback.positive_thanks', 'Thank you for your positive feedback!')
        : newFeedback === 'negative'
        ? t('ai_assistant.feedback.negative_thanks', 'Thank you for your feedback. We\'ll work to improve.')
        : t('ai_assistant.feedback.removed', 'Feedback removed'),
      duration: 2000,
    });
  };

  const handleCopyMessage = async () => {
    if (messageContent) {
      try {
        await navigator.clipboard.writeText(messageContent);
        toast({
          description: t('ai_assistant.message_copied', 'Message copied to clipboard'),
          duration: 2000,
        });
      } catch (error) {
        toast({
          description: t('ai_assistant.copy_failed', 'Failed to copy message'),
          variant: "destructive",
          duration: 2000,
        });
      }
    }
  };

  const handleShareMessage = async () => {
    if (messageContent && navigator.share) {
      try {
        await navigator.share({
          title: 'AI Assistant Message',
          text: messageContent,
        });
      } catch (error) {
        // Fallback to copy if share fails
        handleCopyMessage();
      }
    } else {
      handleCopyMessage();
    }
  };

  const handleRegenerateMessage = () => {
    if (onRegenerateMessage) {
      onRegenerateMessage(messageId);
      toast({
        description: t('ai_assistant.regenerating_message', 'Regenerating message...'),
        duration: 2000,
      });
    }
  };

  // Get topic-specific color
  const getTopicColor = () => {
    switch (topic) {
      case 'guide': return 'botscheme-royal-purple';
      case 'performance': return 'botscheme-ocean-blue';
      case 'trading_strategy': return 'botscheme-emerald-green';
      case 'advisor': return 'botscheme-amber-gold';
      default: return 'botscheme-royal-purple';
    }
  };
  
  const topicColor = getTopicColor();
  
  // Enhanced animation for buttons
  const buttonAnimation = {
    whileHover: { scale: isMobile ? 1 : 1.1, y: -1 },
    whileTap: { scale: 0.95 },
    transition: { type: "spring", stiffness: 400, damping: 25 }
  };
  
  return (
    <div className="flex items-center justify-between gap-2 mt-3 text-muted-foreground text-xs">
      {/* Multi-language indicator */}
      {isMultiLanguageSource && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.span 
                className="flex items-center gap-1 text-blue-500"
                whileHover={{ scale: 1.05 }}
              >
                <Globe className="h-3 w-3" />
                {sourceLanguage && <span className="text-[10px] uppercase font-medium">{sourceLanguage}</span>}
              </motion.span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('ai_assistant.multi_language_source', 'Multi-language source')}</p>
              {sourceLanguage && (
                <p className="text-xs">{t('ai_assistant.source_language', 'Source language')}: {sourceLanguage}</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Action buttons container */}
      <div className="flex items-center gap-1">
        {/* Message actions (copy, share, regenerate) */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="flex items-center gap-1 mr-2"
              initial={{ opacity: 0, scale: 0.8, x: 10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Copy button */}
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 h-auto rounded-full hover:bg-background/80"
                    disabled={disabled || !messageContent}
                    onClick={handleCopyMessage}
                    aria-label={t('ai_assistant.copy_message', 'Copy message')}
                  >
                    <motion.div {...buttonAnimation}>
                      <Copy className="h-3.5 w-3.5" />
                    </motion.div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('ai_assistant.copy_message', 'Copy message')}</p>
                </TooltipContent>
              </Tooltip>

              {/* Share button */}
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 h-auto rounded-full hover:bg-background/80"
                    disabled={disabled || !messageContent}
                    onClick={handleShareMessage}
                    aria-label={t('ai_assistant.share_message', 'Share message')}
                  >
                    <motion.div {...buttonAnimation}>
                      <Share className="h-3.5 w-3.5" />
                    </motion.div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('ai_assistant.share_message', 'Share message')}</p>
                </TooltipContent>
              </Tooltip>

              {/* Regenerate button */}
              {onRegenerateMessage && (
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1.5 h-auto rounded-full hover:bg-background/80"
                      disabled={disabled}
                      onClick={handleRegenerateMessage}
                      aria-label={t('ai_assistant.regenerate_message', 'Regenerate message')}
                    >
                      <motion.div {...buttonAnimation}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </motion.div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('ai_assistant.regenerate_message', 'Regenerate message')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* More actions button */}
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 h-auto rounded-full hover:bg-background/80"
              disabled={disabled}
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={t('ai_assistant.more_actions', 'More actions')}
            >
              <motion.div 
                animate={{ rotate: isExpanded ? 45 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <MessageSquarePlus className="h-3.5 w-3.5" />
              </motion.div>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('ai_assistant.more_actions', 'More actions')}</p>
          </TooltipContent>
        </Tooltip>

        {/* Feedback section */}
        <span className="text-xs">{t('ai_assistant.feedback.helpful')}:</span>
        <div className="flex gap-1">
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'p-1.5 h-auto rounded-full transition-all duration-200',
                  currentFeedback === 'positive' && [
                    `text-${topicColor} bg-${topicColor}/15`,
                    'shadow-md'
                  ]
                )}
                disabled={disabled}
                onClick={() => handleFeedback('positive')}
                aria-label={t('ai_assistant.feedback.helpful_yes')}
              >
                <motion.div {...buttonAnimation}>
                  <ThumbsUp className="h-3.5 w-3.5" />
                </motion.div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('ai_assistant.feedback.helpful_yes', 'Helpful')}</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'p-1.5 h-auto rounded-full transition-all duration-200',
                  currentFeedback === 'negative' && [
                    'text-destructive bg-destructive/15',
                    'shadow-md'
                  ]
                )}
                disabled={disabled}
                onClick={() => handleFeedback('negative')}
                aria-label={t('ai_assistant.feedback.helpful_no')}
              >
                <motion.div {...buttonAnimation}>
                  <ThumbsDown className="h-3.5 w-3.5" />
                </motion.div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('ai_assistant.feedback.helpful_no', 'Not helpful')}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

// Add display name for React DevTools
MessageFeedback.displayName = "MessageFeedback";
